import { OrgLevelTypeEnum } from "../enums/orgLevelType.enum";
import { OrganizationLevel } from "../models/organizationLevel.model";

export function toOrganizationTree(items: OrganizationLevel[], rootLevelType: number = OrgLevelTypeEnum.Other): OrganizationLevel[] {
    if (!items?.length) return [];

    const idSet = new Set(items.map(i => i.id));
    const levelTypes = new Set(items
        .map(x => x.orgLevelType)
        .filter((levelType): levelType is number => levelType != null));
    let effectiveRoot = rootLevelType;
    if (!levelTypes.has(effectiveRoot) && levelTypes.size > 0) {
        effectiveRoot = levelTypes.has(1) ? 1 : Math.min(...levelTypes);
    }

    const lookup = items.reduce((acc, item) => {
        if (item.parentId != null) { (acc[item.parentId] ||= []).push({ ...item }); } return acc;
    }, {} as Record<string, OrganizationLevel[]>);

    const build = (parentId: string, path = new Set<string>()): OrganizationLevel[] => {
        const children = lookup[parentId] || [];
        return children.map(child => {
            if (path.has(child.id)) { return { ...child, children: [] }; }
            const nextPath = new Set(path).add(child.id);
            return { ...child, children: build(child.id, nextPath) };
        });
    };

    const rootsByType = items.filter(x => x.orgLevelType === effectiveRoot && (x.parentId == null || !idSet.has(x.parentId)));
    const orphanRoots = items.filter(x => x.parentId != null && !idSet.has(x.parentId) && x.orgLevelType !== effectiveRoot);

    const uniqRoots = new Map<string, OrganizationLevel>();
    [...rootsByType, ...orphanRoots].forEach(r => uniqRoots.set(r.id, r));

    let roots = Array.from(uniqRoots.values());

    if (!roots.length) { roots = items.filter(x => x.parentId == null); if (!roots.length) { roots = items; } }

    return roots.map(root => ({ ...root, children: build(root.id) }));
}

export function getDescendantOrSelfIds(nodes: OrganizationLevel[], anchorId: string): Set<string> {
    const ids = new Set<string>();

    const collectAll = (items: OrganizationLevel[]): void => {
        items.forEach(item => {
            ids.add(item.id);
            if (item.children?.length) { collectAll(item.children); }
        });
    };

    const findAndCollect = (items: OrganizationLevel[]): boolean => {
        for (const item of items) {
            if (item.id === anchorId) {
                collectAll([item]);
                return true;
            }
            if (item.children?.length && findAndCollect(item.children)) { return true; }
        }
        return false;
    };

    findAndCollect(nodes);
    return ids;
}

export function filterOrganizationTree(
    nodes: OrganizationLevel[],
    term: string
): OrganizationLevel[] {
    return nodes.reduce<OrganizationLevel[]>((acc, node) => {
        const childMatches = node.children?.length
            ? filterOrganizationTree(node.children, term)
            : [];
        const codeMatch = node.code != null
            ? String(node.code).toLowerCase().includes(term)
            : false;
        if (
            node.name?.toLowerCase().includes(term) ||
            codeMatch ||
            childMatches.length > 0
        ) {
            acc.push({
                ...node,
                children: childMatches.length ? childMatches : node.children,
            });
        }
        return acc;
    }, []);
}
